import { AdminCohorts, CorporateClients } from '@platform/client';
import { AdminCohort, CohortType } from '@platform/client/schemas';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useContext, useMemo, useState } from 'react';
import { AdminListPageWrapper } from '../../AdminListPage';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { CreateCohortModal } from './CreateCohortModal';
import { Link } from 'wouter';
import { ADMIN_ROUTES, ROUTES } from '../../../routes/consts';
import { CorporateLink } from '../corporates/CorporatesAdmin';

export const CohortsAdmin = () => {
  return (
    <AdminListPageWrapper>
      <CohortsAdminContent />
    </AdminListPageWrapper>
  );
};

const columnHelper = createColumnHelper<AdminCohort>();

const CohortsAdminContent = () => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [createCohortIsOpen, setCreateCohortIsOpen] = useState(false);
  const { data, isLoading, isSuccess, isError } = AdminCohorts.useGetAdminCohorts(
    adminQueryOptionsV2,
    {
      query: { placeholderData: keepPreviousData },
    },
  );
  const { cohorts: cohortsData = [], filteredCount = 0 } = data || {};

  const { data: clientFilterData } = CorporateClients.useGetAllCorporateClients();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          header: 'ID',
          cell: (id) => id.getValue(),
          meta: {
            filterVariant: 'text',
          },
          size: 25,
        }),
        columnHelper.accessor('name', {
          header: 'Name',
          cell: ({ row }) => <CohortLink cohortId={row.original.id} name={row.original.name} />,
          meta: {
            filterVariant: 'text',
          },
          size: 250,
        }),
        columnHelper.accessor('clientName', {
          header: 'Corporate Client',
          cell: ({ row }) => (
            <CorporateLink
              clientId={row.original.clientId}
              clientName={row.original.clientName}
              cohortId={row.original.id}
            />
          ),
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions: clientFilterData?.map((client) => ({
              label: client.name,
              value: client.id,
            })),
          },
          size: 250,
        }),
        columnHelper.accessor('template', {
          header: 'Cohort Template',
          cell: (template) => template.getValue()?.cohortType || 'N/A',
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions: [...Object.values(CohortType), 'None'].map((cohort_type) => ({
              label: cohort_type,
              value: cohort_type,
            })),
          },
        }),
        columnHelper.accessor('applicationStartDate', {
          header: 'Application Start Date',
          cell: (applicationStartDate) => {
            const date = applicationStartDate.getValue();
            if (!date) {
              return 'N/A';
            }
            return new Date(date).toLocaleDateString();
          },
          enableColumnFilter: false,
          size: 150,
        }),
        columnHelper.accessor('applicationEndDate', {
          header: 'Application End Date',
          cell: (applicationEndDate) => {
            const date = applicationEndDate.getValue();
            if (!date) {
              return 'N/A';
            }
            return new Date(date).toLocaleDateString();
          },
          enableColumnFilter: false,
          size: 150,
        }),
        columnHelper.accessor('startDate', {
          header: 'Start Date',
          cell: (startDate) => {
            const date = startDate.getValue();
            if (!date) {
              return 'N/A';
            }
            return new Date(date).toLocaleDateString();
          },
          enableColumnFilter: false,
          size: 150,
        }),
        columnHelper.accessor('kickoffDate', {
          header: 'Kickoff Date',
          cell: (kickoffDate) => {
            const date = kickoffDate.getValue();
            if (!date) {
              return 'N/A';
            }
            return new Date(date).toLocaleDateString();
          },
          enableColumnFilter: false,
          size: 150,
        }),
        columnHelper.accessor('endDate', {
          header: 'End Date',
          cell: (endDate) => {
            const date = endDate.getValue();
            if (!date) {
              return 'N/A';
            }
            return new Date(date).toLocaleDateString();
          },
          enableColumnFilter: false,
          size: 150,
        }),
        columnHelper.accessor('createdAt', {
          header: 'Created At',
          cell: (createdAt) => new Date(createdAt.getValue()).toLocaleDateString(),
          meta: {
            filterVariant: 'date-select',
          },
          size: 150,
        }),
        columnHelper.accessor('updatedAt', {
          header: 'Updated At',
          cell: (updatedAt) => new Date(updatedAt.getValue()).toLocaleDateString(),
          meta: {
            filterVariant: 'date-select',
          },
          size: 150,
        }),
      ] as ColumnDef<AdminCohort>[],
    [clientFilterData],
  );
  return (
    <>
      <AdminTable
        columns={columns}
        data={cohortsData}
        filteredCount={filteredCount}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        enableFiltering={true}
        tableActions={[{ text: 'Create Cohort', callback: () => setCreateCohortIsOpen(true) }]}
      />
      {createCohortIsOpen && <CreateCohortModal onClose={() => setCreateCohortIsOpen(false)} />}
    </>
  );
};

export const CohortLink = ({
  cohortId,
  name,
}: {
  cohortId?: number | null;
  name?: string | null;
}) => {
  if (cohortId && name) {
    return (
      <Link
        to={`~/platform${ROUTES.ADMIN}${ADMIN_ROUTES.COHORTS}/${cohortId}`}
        className='text-brand-500 hover:underline'
      >
        {name}
      </Link>
    );
  }
};
